import React, {useState, useEffect} from 'react';
import Calendar from 'react-calendar';
import {useLocation} from 'react-router-dom';
import {useDatabase} from '../context/DatabaseContext';
import {colors} from '../theme/colors';
import 'react-calendar/dist/Calendar.css';

const HistoryScreen = () => {
  const {getEntriesByDate, getAllDates} = useDatabase();
  const location = useLocation();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({
    calories: 0,
    protein: 0,
    carbs: 0,
    fat: 0,
  });
  const [markedDates, setMarkedDates] = useState(new Set());
  const [refreshing, setRefreshing] = useState(false);

  // Default targets
  const targets = {
    calories: 2000,
    protein: 150,
    carbs: 200,
    fat: 65,
  };

  useEffect(() => {
    if (location.pathname === '/history') {
      loadDates();
      loadEntriesForDate(selectedDate);
    }
  }, [location.pathname, selectedDate]);

  const loadDates = async () => {
    const dates = await getAllDates();
    const markedSet = new Set(dates);
    setMarkedDates(markedSet);
  };

  const loadEntriesForDate = async date => {
    const dateString = date.toISOString().split('T')[0];
    const dateEntries = await getEntriesByDate(dateString);
    setEntries(dateEntries);

    const totalsCalc = dateEntries.reduce(
      (acc, entry) => ({
        calories: acc.calories + entry.calories,
        protein: acc.protein + entry.protein,
        carbs: acc.carbs + entry.carbs,
        fat: acc.fat + entry.fat,
      }),
      {calories: 0, protein: 0, carbs: 0, fat: 0},
    );

    setTotals(totalsCalc);
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadDates();
    await loadEntriesForDate(selectedDate);
    setRefreshing(false);
  };

  const onDayClick = date => {
    setSelectedDate(date);
    loadEntriesForDate(date);
  };

  const formatDate = date => {
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  };

  const getProgress = (current, target) => {
    return Math.min((current / target) * 100, 100);
  };

  const ProgressBar = ({progress, color, gradient}) => (
    <div style={styles.progressBarContainer}>
      <div 
        style={{
          ...styles.progressBar, 
          width: `${progress}%`, 
          background: gradient || `linear-gradient(90deg, ${color} 0%, ${color}dd 100%)`
        }} 
      />
    </div>
  );

  const tileClassName = ({date, view}) => {
    if (view === 'month') {
      const dateString = date.toISOString().split('T')[0];
      const isMarked = markedDates.has(dateString);
      const isSelected = date.toDateString() === selectedDate.toDateString();
      
      if (isSelected) {
        return 'react-calendar__tile--selected';
      }
      if (isMarked) {
        return 'react-calendar__tile--marked';
      }
    }
    return null;
  };

  const tileContent = ({date, view}) => {
    if (view === 'month') {
      const dateString = date.toISOString().split('T')[0];
      if (markedDates.has(dateString)) {
        return <div style={styles.calendarDot} />;
      }
    }
    return null;
  };

  return (
    <div style={styles.container}>
      <style>{`
        .react-calendar {
          background-color: ${colors.surface};
          border: 1px solid ${colors.border};
          border-radius: 15px;
          color: ${colors.textPrimary};
          font-family: inherit;
        }
        .react-calendar__navigation {
          background-color: ${colors.surface};
          border-bottom: 1px solid ${colors.border};
        }
        .react-calendar__navigation button {
          color: ${colors.textPrimary};
          font-weight: bold;
        }
        .react-calendar__navigation button:enabled:hover,
        .react-calendar__navigation button:enabled:focus {
          background-color: ${colors.surfaceAlt};
        }
        .react-calendar__month-view__weekdays {
          background-color: ${colors.surface};
          color: ${colors.textPrimary};
          font-weight: 600;
        }
        .react-calendar__month-view__weekdays__weekday {
          padding: 0.5em;
        }
        .react-calendar__tile {
          color: ${colors.textPrimary};
          background-color: ${colors.surface};
        }
        .react-calendar__tile:enabled:hover,
        .react-calendar__tile:enabled:focus {
          background-color: ${colors.surfaceAlt};
        }
        .react-calendar__tile--active {
          background-color: ${colors.primary};
          color: ${colors.textOnPrimary};
        }
        .react-calendar__tile--now {
          background-color: ${colors.bg};
        }
        .react-calendar__tile--marked {
          position: relative;
        }
      `}</style>
      <div style={styles.scrollView}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.profileSection}>
            <div style={styles.profileIcon}>
              <span style={styles.profileIconText}>💪</span>
            </div>
            <div style={styles.welcomeSection}>
              <div style={styles.welcomeText}>ברוכים הבאים</div>
              <div style={styles.userName}>יובל ❤️</div>
            </div>
          </div>
        </div>

        {/* Calendar */}
        <div style={styles.calendarSection}>
          <Calendar
            onChange={onDayClick}
            value={selectedDate}
            tileClassName={tileClassName}
            tileContent={tileContent}
            className="custom-calendar"
          />
        </div>

        {/* Selected Date */}
        <div style={styles.dateSection}>
          <button
            style={styles.dateButton}
            onClick={() => {
              const prevDate = new Date(selectedDate);
              prevDate.setDate(prevDate.getDate() - 1);
              setSelectedDate(prevDate);
            }}>
            <span style={styles.dateArrow}>←</span>
          </button>
          <div style={styles.dateText}>{formatDate(selectedDate)}</div>
          <button
            style={styles.dateButton}
            onClick={() => {
              const nextDate = new Date(selectedDate);
              nextDate.setDate(nextDate.getDate() + 1);
              setSelectedDate(nextDate);
            }}>
            <span style={styles.dateArrow}>→</span>
          </button>
          <button
            style={styles.refreshButton}
            onClick={onRefresh}
            disabled={refreshing}
            className="btn-secondary"
            aria-label="Refresh entries">
            {refreshing ? 'מרענן...' : '🔄 רענן'}
          </button>
        </div>

        {/* Daily Totals Cards */}
        <div style={styles.totalsGrid}>
          <div style={styles.totalCard} className="card-hover">
            <div style={{...styles.totalValue, color: colors.protein}}>
              {totals.protein.toFixed(1)}/{targets.protein}
            </div>
            <div style={styles.totalLabel}>סה"כ חלבון (גרם)</div>
            <ProgressBar
              progress={getProgress(totals.protein, targets.protein)}
              color={colors.protein}
              gradient={`linear-gradient(90deg, ${colors.protein} 0%, ${colors.primaryLight} 100%)`}
            />
          </div>

          <div style={styles.totalCard} className="card-hover">
            <div style={{...styles.totalValue, color: colors.calories}}>
              {totals.calories.toFixed(1)}/{targets.calories}
            </div>
            <div style={styles.totalLabel}>סה"כ קלוריות</div>
            <ProgressBar
              progress={getProgress(totals.calories, targets.calories)}
              color={colors.calories}
              gradient={`linear-gradient(90deg, ${colors.calories} 0%, #FFC966 100%)`}
            />
          </div>

          <div style={styles.totalCard} className="card-hover">
            <div style={{...styles.totalValue, color: colors.fat}}>
              {totals.fat.toFixed(1)}/{targets.fat}
            </div>
            <div style={styles.totalLabel}>סה"כ שומן (גרם)</div>
            <ProgressBar
              progress={getProgress(totals.fat, targets.fat)}
              color={colors.fat}
              gradient={`linear-gradient(90deg, ${colors.fat} 0%, #FF8FA3 100%)`}
            />
          </div>

          <div style={styles.totalCard} className="card-hover">
            <div style={{...styles.totalValue, color: colors.carbs}}>
              {totals.carbs.toFixed(1)}/{targets.carbs}
            </div>
            <div style={styles.totalLabel}>סה"כ פחמימות (גרם)</div>
            <ProgressBar
              progress={getProgress(totals.carbs, targets.carbs)}
              color={colors.carbs}
              gradient={`linear-gradient(90deg, ${colors.carbs} 0%, ${colors.primaryLight} 100%)`}
            />
          </div>
        </div>

        {/* Meal Entries */}
        <div style={styles.entriesSection}>
          <div style={styles.sectionTitle}>יומן ארוחות</div>
          {entries.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyStateIcon}>📅</div>
              <div style={styles.emptyStateTitle}>אין רשומות לתאריך זה</div>
              <div style={styles.emptyStateText}>
                בחר תאריך אחר או הוסף רשומות במסך ההוספה!
              </div>
            </div>
          ) : (
            entries.map((entry, index) => (
              <div 
                key={entry.id} 
                style={{...styles.entryCard, animationDelay: `${index * 0.05}s`}} 
                className="card-hover list-item">
                <div style={styles.entryHeader}>
                  <div style={styles.entryFoodName}>{entry.food_name}</div>
                  <div style={styles.entryGrams}>{entry.grams.toFixed(1)}g</div>
                </div>
                <div style={styles.entryNutrition}>
                  <div style={styles.entryNutritionItem}>
                    <div style={{...styles.entryNutritionValue, color: colors.calories}}>
                      {entry.calories.toFixed(1)}
                    </div>
                    <div style={styles.entryNutritionLabel}>קל</div>
                  </div>
                  <div style={styles.entryNutritionItem}>
                    <div style={{...styles.entryNutritionValue, color: colors.protein}}>
                      {entry.protein.toFixed(1)}g
                    </div>
                    <div style={styles.entryNutritionLabel}>חלבון</div>
                  </div>
                  <div style={styles.entryNutritionItem}>
                    <div style={{...styles.entryNutritionValue, color: colors.carbs}}>
                      {entry.carbs.toFixed(1)}g
                    </div>
                    <div style={styles.entryNutritionLabel}>פחמימות</div>
                  </div>
                  <div style={styles.entryNutritionItem}>
                    <div style={{...styles.entryNutritionValue, color: colors.fat}}>
                      {entry.fat.toFixed(1)}g
                    </div>
                    <div style={styles.entryNutritionLabel}>שומן</div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    flex: 1,
    backgroundColor: colors.bg,
    minHeight: '100vh',
  },
  scrollView: {
    flex: 1,
    paddingBottom: 20,
    overflowY: 'auto',
  },
  header: {
    paddingLeft: 20,
    paddingRight: 20,
    paddingTop: 10,
    paddingBottom: 20,
  },
  profileSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  profileIcon: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: colors.surface,
    border: `2px solid ${colors.primary}`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 15,
  },
  profileIconText: {
    fontSize: 30,
  },
  welcomeSection: {
    flex: 1,
  },
  welcomeText: {
    color: colors.textTertiary,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.5,
  },
  userName: {
    color: colors.textPrimary,
    fontSize: 24,
    fontWeight: 'bold',
    marginTop: 4,
    lineHeight: 1.2,
  },
  calendarSection: {
    backgroundColor: colors.surface,
    marginLeft: 20,
    marginRight: 20,
    marginBottom: 20,
    borderRadius: 15,
    overflow: 'hidden',
    border: `1px solid ${colors.border}`,
    padding: 10,
  },
  calendarDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.primary,
    position: 'absolute',
    bottom: 4,
    left: '50%',
    transform: 'translateX(-50%)',
  },
  dateSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
    paddingLeft: 20,
    paddingRight: 20,
    gap: 10,
  },
  dateText: {
    color: colors.textPrimary,
    fontSize: 16,
    marginLeft: 15,
    marginRight: 15,
  },
  dateButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 5,
  },
  dateArrow: {
    color: colors.primary,
    fontSize: 20,
    fontWeight: 'bold',
  },
  refreshButton: {
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    padding: '10px 20px',
    color: colors.primary,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 200ms ease',
    height: 40,
  },
  totalsGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingLeft: 15,
    paddingRight: 15,
    marginBottom: 20,
  },
  totalCard: {
    width: 'calc(50% - 6px)',
    backgroundColor: colors.surface,
    borderRadius: 16,
    padding: 24,
    marginBottom: 12,
    border: `1px solid ${colors.border}`,
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    transition: 'all 200ms ease',
  },
  totalValue: {
    fontSize: 22,
    fontWeight: 'bold',
    marginBottom: 8,
    lineHeight: 1.2,
    transition: 'color 200ms ease',
  },
  totalLabel: {
    fontSize: 12,
    color: colors.textTertiary,
    marginBottom: 16,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontWeight: 500,
  },
  progressBarContainer: {
    height: 8,
    backgroundColor: colors.surfaceAlt,
    borderRadius: 4,
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    borderRadius: 4,
    transition: 'width 500ms cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
  },
  entriesSection: {
    paddingLeft: 20,
    paddingRight: 20,
    paddingBottom: 30,
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: colors.textPrimary,
    marginBottom: 20,
    lineHeight: 1.2,
  },
  emptyState: {
    backgroundColor: colors.surface,
    padding: 48,
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${colors.border}`,
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    textAlign: 'center',
  },
  emptyStateIcon: {
    fontSize: 64,
    marginBottom: 16,
    opacity: 0.5,
  },
  emptyStateTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: colors.textPrimary,
    marginBottom: 8,
    lineHeight: 1.2,
  },
  emptyStateText: {
    fontSize: 16,
    color: colors.textTertiary,
    textAlign: 'center',
    lineHeight: 1.6,
    whiteSpace: 'pre-line',
  },
  entryCard: {
    backgroundColor: colors.surface,
    marginBottom: 12,
    padding: 20,
    borderRadius: 16,
    border: `1px solid ${colors.border}`,
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    transition: 'all 200ms ease',
  },
  entryHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  entryFoodName: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.textPrimary,
    flex: 1,
  },
  entryGrams: {
    fontSize: 14,
    color: colors.textTertiary,
    fontWeight: '500',
  },
  entryNutrition: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingTop: 12,
    borderTop: `1px solid ${colors.border}`,
  },
  entryNutritionItem: {
    display: 'flex',
    alignItems: 'center',
  },
  entryNutritionValue: {
    fontSize: 14,
    fontWeight: '600',
    lineHeight: 1.2,
    transition: 'color 200ms ease',
  },
  entryNutritionLabel: {
    fontSize: 10,
    color: colors.textSecondary,
    marginTop: 4,
    textTransform: 'uppercase',
  },
};

export default HistoryScreen;
